/**
 * `TRACE_animation_binding` — the node extension object, read and written.
 *
 * ```json
 * "extensions": { "TRACE_animation_binding": { "sources": [ { "externalAsset": 1, "root": "spin" } ] } }
 * ```
 *
 * Pure: no Babylon.
 */

import { TRACE_ANIMATION_BINDING } from './names';

/** One `sources[]` entry, as on the wire. */
export interface WireBindingSource {
    externalAsset: number;
    /** Name of the source node whose TRS channels offset the binding node. */
    root?: string;
}

/** The node extension object, as on the wire. */
export interface WireAnimationBinding {
    sources: WireBindingSource[];
}

/** The node extension object, read. */
export interface ReadAnimationBinding {
    sources?: WireBindingSource[];
    /** What was wrong with it (dropped entries). */
    problems: string[];
}

function asObject(value: unknown): Record<string, unknown> | undefined {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, unknown>
        : undefined;
}

/**
 * Read `node.extensions.TRACE_animation_binding`. Undefined when the node has
 * none. A malformed entry (no integer `externalAsset`, a `root` that is not a
 * non-empty string) is dropped with a problem; the rest stand.
 */
export function readNodeAnimationBinding(nodeExtensions: unknown): ReadAnimationBinding | undefined {
    const ext = asObject(asObject(nodeExtensions)?.[TRACE_ANIMATION_BINDING]);
    if (!ext) return undefined;
    const problems: string[] = [];
    if (ext.sources === undefined) return { problems };
    if (!Array.isArray(ext.sources)) {
        problems.push('`sources` is not an array');
        return { problems };
    }
    const sources: WireBindingSource[] = [];
    ext.sources.forEach((entry, i) => {
        const source = asObject(entry);
        const externalAsset = source?.externalAsset;
        if (!source || !Number.isInteger(externalAsset) || (externalAsset as number) < 0) {
            problems.push(`sources[${i}] has no valid externalAsset`);
            return;
        }
        if (source.root !== undefined && (typeof source.root !== 'string' || source.root === '')) {
            problems.push(`sources[${i}].root ${JSON.stringify(source.root)} is not a node name`);
            return;
        }
        sources.push({ externalAsset: externalAsset as number, ...(source.root !== undefined ? { root: source.root } : {}) });
    });
    return { sources, problems };
}
